<template>
  <Transition name="drawer">
    <div
      v-if="isOpen"
      class="fixed inset-0 drawer-wrapper"
    >
      <!-- Backdrop/overlay - full width on mobile, partial on desktop -->
      <div
        class="fixed inset-y-0 right-0 bg-surface-50 dark:bg-surface-950 drawer-overlay"
        style="width: 100vw; min-width: 600px; max-width: 700px;"
        @click="$emit('close')"
      />

      <!-- Drawer container - purposely NOT using tech-panel class -->
      <div class="fixed top-[60px] bottom-0 right-0 sci-fi-drawer">
        <!-- Header -->
        <div class="p-4 border-b border-primary-500/30 flex justify-between items-center sci-fi-drawer-header">
          <div class="flex items-center">
            <Button
              v-if="canGoBack"
              icon="pi pi-arrow-left"
              class="p-button-text p-button-rounded mr-2 tech-button"
              @click="$emit('back')"
            />
            <h3 class="text-xl font-semibold videophreak text-primary-600 dark:text-primary-400">
              {{ title }}
            </h3>
            <Button
              v-if="content?.data?.id"
              v-tooltip.top="'Copy UID to clipboard'"
              icon="pi pi-copy"
              class="p-button-text p-button-rounded p-button ml-2 tech-button text-surface-400 dark:text-surface-500"
              aria-label="Copy UID"
              @click="copyIdToClipboard(content.data.id)"
            />
          </div>
          <Button
            icon="pi pi-times"
            class="p-button-text p-button-rounded tech-button"
            @click="$emit('close')"
          />
        </div>

        <!-- Content area -->
        <div class="drawer-content p-4">
          <component
            :is="content?.component"
            v-if="content?.component"
            :data="content?.data"
            v-bind="contentProps || {}"
            @close="$emit('close')"
            @reset-history="$emit('reset-history')"
            @update:title="updateDrawerTitle"
          />
          <div
            v-else
            class="p-4 text-center text-surface-600"
          >
            Select an item to view details
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast'
import { onMounted, onUnmounted } from 'vue'

const toast = useToast()
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  content: {
    type: Object,
    default: null,
  },
  contentProps: {
    type: Object,
    default: () => ({}),
  },
  canGoBack: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'back', 'reset-history'])

// When a child component updates the title, pass it to useDrawer
const updateDrawerTitle = (newTitle) => {
  const { updateDrawerTitle } = useDrawer()
  if (updateDrawerTitle) {
    updateDrawerTitle(newTitle)
  }
}

// Add escape key handler
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

// Add and remove event listener for escape key
onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})

// Copy ID to clipboard with feedback
const copyIdToClipboard = (id) => {
  navigator.clipboard.writeText(id)
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Copied to clipboard',
        detail: 'UID copied to clipboard',
        life: 2000,
      })
    })
    .catch(err => console.error('Could not copy ID to clipboard:', err))
}
</script>

<style scoped>
/* Create a specific z-index stacking context for the drawer */
.drawer-wrapper {
  z-index: 10; /* Extremely high z-index */
  pointer-events: none; /* Allow clicks to pass through by default */
}

/* Make only the overlay and drawer clickable */
.drawer-overlay,
.sci-fi-drawer {
  pointer-events: auto;
}

.drawer-overlay {
  /* Remove the glow border from the overlay */
  box-shadow: none;
}

/* Drawer styling without relying on tech-panel - make fully opaque */
.sci-fi-drawer {
  width: 100%; /* Full width on mobile */
  max-width: 100vw; /* Full viewport width on mobile */
  background-color: var(--surface-0); /* Light mode background */
  border-left: 2px solid var(--primary-500);
  z-index: 110; /* Higher than overlay */
  display: flex;
  flex-direction: column;
  transform: translateX(0);
  transition: transform 0.3s ease;
  /* Add this to ensure opacity */
  backdrop-filter: none;
  /* Move the glow border to the drawer itself so it translates with it */
  box-shadow: -5px 0 10px rgba(57, 255, 20, 0.5), -2px 0 6px rgba(57, 255, 20, 0.2);
}

/* Desktop styling for the drawer - limit width on larger screens */
@media (min-width: 768px) {
  .sci-fi-drawer {
    width: 700px;
    max-width: 90vw;
  }
}

/* Dark mode enhancement - make fully opaque */
:deep(.dark) .sci-fi-drawer {
  background-color: var(--surface-900); /* Dark mode background */
  border-left: 2px solid rgba(57, 255, 20, 0.5);
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.5), -2px 0 8px rgba(57, 255, 20, 0.2);
}

/* Header with sci-fi styling but NO clip-path - ensure it's opaque */
.sci-fi-drawer-header {
  background: var(--surface-card);
  position: relative;
}

:deep(.dark) .sci-fi-drawer-header {
  background: rgba(10, 16, 24, 1); /* Change to fully opaque */
}

/* Content area with scroll */
.drawer-content {
  flex: 1;
  overflow-y: auto;
  height: calc(100% - 60px);
}

/* Drawer animation */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

/* Animate the entire drawer as one unit instead of having the overlay fade in separately */
.drawer-enter-active .drawer-overlay,
.drawer-leave-active .drawer-overlay {
  transition: transform 0.3s ease;
}

.drawer-enter-from .drawer-overlay,
.drawer-leave-to .drawer-overlay {
  transform: translateX(100%);
}

.drawer-enter-active .sci-fi-drawer,
.drawer-leave-active .sci-fi-drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from .sci-fi-drawer,
.drawer-leave-to .sci-fi-drawer {
  transform: translateX(100%);
}
</style>
